import { Link } from 'react-router-dom';
import LineCtaButton from '@/pages/gmb-audit/consult/components/LineCtaButton';
import type { ConsultReportContext } from '@/pages/gmb-audit/consult/utils/consultContext';

type CtaVariant = 'light' | 'medium';

/**
 * 報告內嵌 CTA（V1 Conversion-1）。
 * CTA #1 = light（Health Overview + 4 大構面後）；CTA #2 = medium（Top 3 之後）。
 * Primary 一律導向 /gmb-audit/consult，並以 React Router navigation state 傳遞
 * 最少必要 context（絕不放入 URL）。
 */
export default function ReportInlineCta({
  variant,
  eyebrow,
  title,
  body,
  primaryLabel,
  context,
}: {
  variant: CtaVariant;
  eyebrow?: string;
  title: string;
  body: string;
  primaryLabel: string;
  context: ConsultReportContext | null;
}) {
  const isMedium = variant === 'medium';

  const shell = isMedium
    ? 'rounded-2xl border border-primary-200 bg-primary-50/70 p-5 shadow-card md:p-7'
    : 'rounded-2xl border border-background-300 bg-background-100 p-5 md:p-6';

  const iconWrap = isMedium
    ? 'bg-primary-100 text-primary-800'
    : 'bg-background-50 text-primary-700 ring-1 ring-background-300';

  return (
    <section className="ds-container pt-10 md:pt-12">
      <div className={shell}>
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between md:gap-8">
          <div className="flex items-start gap-3.5">
            <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${iconWrap}`}>
              <i className="ri-chat-smile-2-line text-xl" />
            </span>
            <div>
              {eyebrow && (
                <p className="text-sm font-semibold uppercase tracking-wide text-primary-700">{eyebrow}</p>
              )}
              <h3 className={`mt-0.5 font-bold text-foreground-950 ${isMedium ? 'text-lg md:text-xl' : 'text-lg'}`}>
                {title}
              </h3>
              <p className="mt-2 max-w-2xl text-base leading-relaxed text-foreground-600">{body}</p>
            </div>
          </div>

          <div className="flex shrink-0 flex-col gap-3 sm:flex-row md:flex-col lg:flex-row">
            <Link
              to="/gmb-audit/consult"
              state={{ consultContext: context }}
              className={`inline-flex h-12 w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-xl px-5 text-base font-semibold transition-colors sm:w-auto ${
                isMedium
                  ? 'bg-primary-700 text-background-50 hover:bg-primary-800'
                  : 'border border-primary-300 bg-background-50 text-primary-800 hover:bg-primary-50'
              }`}
            >
              <i className="ri-arrow-right-line text-lg" />
              {primaryLabel}
            </Link>
            <LineCtaButton size="md" fullWidth className="sm:w-auto" />
          </div>
        </div>
      </div>
    </section>
  );
}